/**
 * NOTE: This page is used for handling redirects from Stripe's hosted checkout page (legacy flow).
 * For subscriptions, we now use embedded checkout within a modal (see pricing/index.tsx).
 * This page is kept for backward compatibility and for one-time payment flows.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { billing } from '@services/billing/billing-service'
import { SubscriptionData } from '@interfaces/auth'
import useAppState from '../../contexts/state'
import { AuthState } from '../../contexts/slices/auth'
import { LoaderCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { useQuery } from '@/utils/utils'
import { AnalyticsService } from '@/services/analytics'

const CheckoutCallback = props => {
  document.title = 'Checkout'

  const [loading, setLoading] = useState(false)
  const [documentId, setDocumentId] = useState('')

  const { setSubscription } = useAppState((state: unknown) => ({
    setSubscription: (state as AuthState).setSubscription,
  }))

  const subscriptionLoadingText = 'Please wait while we setup your subscription...'
  const paymentSuccessText = 'Please wait while we setup your contract review...'

  const [loadingText, setLoadingText] = useState('')

  const query = useQuery()

  const navigate = useNavigate()

  const fetchSubscriptionByPolling = async () => {
    const response = await billing.checkoutCallback()
    if (response.status === 200) {
      const subscription = response.data as SubscriptionData
      if (subscription && subscription.id) {
        setSubscription(subscription)
        return true
      }
    }
    return false
  }

  const fetchPaymentStatusByPolling = async (sessionId: string) => {
    const response = await billing.checkoutCallback(sessionId)
    if (response.status === 200) {
      const status = response.data as any
      if (status && status.public_id) {
        return status.public_id
      }
    }
    return false
  }

  useEffect(() => {
    setLoading(true)
    if (query.get('session_id')) {
      const sessionId = query.get('session_id') as string
      setLoadingText(paymentSuccessText)
      const intervalId = setInterval(async () => {
        try {
          const documentId = await fetchPaymentStatusByPolling(sessionId)
          if (documentId) {
            AnalyticsService.trackPurchase()
            clearInterval(intervalId)
            navigate(`/review/${documentId}`)
          }
        } catch (error) {
          clearInterval(intervalId)
          const documentId = query.get('document_id')
          toast.error(error.data.error)
          navigate(`/review/${documentId}`)
        }
      }, 2000)

      return () => clearInterval(intervalId)
    } else {
      setLoadingText(subscriptionLoadingText)
      const intervalId = setInterval(async () => {
        const isSubscribed = await fetchSubscriptionByPolling()
        if (isSubscribed) {
          AnalyticsService.trackPurchase()
          clearInterval(intervalId)
          navigate(`/dashboard`)
        }
      }, 2000)

      return () => clearInterval(intervalId)
    }
  }, [])

  return (
    <div className="flex min-h-[88vh] items-center justify-center">
      <p className="flex items-center space-x-2">
        <LoaderCircle className="h-4 w-4 animate-spin" />
        <span>{loadingText}</span>
      </p>
    </div>
  )
}

export default CheckoutCallback
